export const CLIENT_LAYOUT_BLOCKS = ['menu', 'review', 'social', 'promo', 'campaign', 'rules'];
export const CLIENT_LAYOUT_ALLOWED_SIZES = ['sm', 'md', 'lg', 'xl'];
export const CLIENT_LAYOUT_ALLOWED_STYLES = ['glass', 'ghost', 'primary'];
export const HERO_TOP_SECTIONS = ['promo', 'duo'];
const HERO_ORDER_BLOCKS = ['promo', 'menu', 'review', 'social'];

export const DEFAULT_CLIENT_LAYOUT_CONFIG = {
  heroTopSection: 'promo',
  order: ['menu', 'review', 'social', 'promo', 'campaign', 'rules'],
  sizes: {
    menu: 'lg',
    review: 'md',
    social: 'md',
    promo: 'md',
    campaign: 'xl',
    rules: 'sm'
  },
  highlight: {
    menu: false,
    review: false,
    social: false,
    promo: true,
    campaign: true,
    rules: false
  },
  style: {
    menu: 'glass',
    review: 'glass',
    social: 'glass',
    promo: 'glass',
    campaign: 'primary',
    rules: 'ghost'
  },
  visible: {
    menu: true,
    review: true,
    social: true,
    promo: true,
    campaign: true,
    rules: true
  },
  offsetX: {
    campaign: 0,
    rules: 0
  }
};

export const CLIENT_LAYOUT_PRESETS = {
  restaurant_promo: {
    order: ['menu', 'promo', 'review', 'social', 'campaign', 'rules'],
    sizes: { promo: 'lg', campaign: 'xl' },
    highlight: { promo: true, menu: true, campaign: true },
    style: { promo: 'glass', campaign: 'primary' },
    visible: {}
  },
  salon_elegant: {
    order: ['menu', 'review', 'promo', 'social', 'campaign', 'rules'],
    sizes: { menu: 'md', promo: 'md', campaign: 'lg' },
    highlight: { promo: true, social: false },
    style: { menu: 'glass', review: 'ghost', social: 'ghost', promo: 'glass', campaign: 'primary' },
    visible: {}
  },
  kids_playful: {
    order: ['promo', 'menu', 'social', 'review', 'campaign', 'rules'],
    sizes: { promo: 'lg', menu: 'lg', campaign: 'xl' },
    highlight: { promo: true, social: true, campaign: true },
    style: { promo: 'primary', social: 'primary', review: 'glass', menu: 'glass', campaign: 'primary' },
    visible: {}
  }
};

function isDevBrowser() {
  try {
    const h = String(window?.location?.hostname || '');
    return h.includes('localhost') || h.includes('127.0.0.1') || h.includes('staging');
  } catch {
    return false;
  }
}

function warnCorrections(corrections, logger = console.warn) {
  if (!corrections.length || !isDevBrowser()) return;
  logger('[clientLayoutConfig] Invalid config auto-corrected:', corrections.join(', '));
}

/** promo = Oferte deasupra duo; duo = Meniu+Recenzie deasupra Oferte */
export function deriveHeroTopSection(order) {
  const o = Array.isArray(order) ? order : [];
  const promoIdx = o.indexOf('promo');
  const duoIdx = ['menu', 'review', 'social']
    .map((id) => o.indexOf(id))
    .filter((i) => i >= 0);
  if (promoIdx < 0 || duoIdx.length === 0) return 'promo';
  const duoFirst = Math.min(...duoIdx);
  return promoIdx < duoFirst ? 'promo' : 'duo';
}

export function applyHeroTopSectionToOrder(order, heroTopSection) {
  const o = Array.isArray(order) ? [...order] : [...DEFAULT_CLIENT_LAYOUT_CONFIG.order];
  const promoOrdered = o.filter((id) => id === 'promo');
  const duoOrdered = o.filter((id) => ['menu', 'review', 'social'].includes(id));
  const rest = o.filter((id) => !HERO_ORDER_BLOCKS.includes(id));
  const top = heroTopSection === 'duo' ? 'duo' : 'promo';
  return top === 'duo'
    ? [...duoOrdered, ...promoOrdered, ...rest]
    : [...promoOrdered, ...duoOrdered, ...rest];
}

export function validateClientLayoutConfigStrict(rawConfig) {
  const cfg = rawConfig && typeof rawConfig === 'object' ? rawConfig : {};
  const errors = [];
  const order = Array.isArray(cfg.order) ? cfg.order : [];
  const seen = new Set();
  order.forEach((id) => {
    if (!CLIENT_LAYOUT_BLOCKS.includes(id)) errors.push(`order_invalid_id:${String(id)}`);
    if (seen.has(id)) errors.push(`order_duplicate:${String(id)}`);
    seen.add(id);
  });
  CLIENT_LAYOUT_BLOCKS.forEach((id) => {
    const s = cfg?.sizes?.[id];
    if (s !== undefined && !CLIENT_LAYOUT_ALLOWED_SIZES.includes(String(s).trim().toLowerCase())) {
      errors.push(`size_invalid:${id}`);
    }
    const st = cfg?.style?.[id];
    if (st !== undefined && !CLIENT_LAYOUT_ALLOWED_STYLES.includes(String(st).trim().toLowerCase())) {
      errors.push(`style_invalid:${id}`);
    }
    const h = cfg?.highlight?.[id];
    if (h !== undefined && typeof h !== 'boolean') errors.push(`highlight_not_boolean:${id}`);
    const v = cfg?.visible?.[id];
    if (v !== undefined && typeof v !== 'boolean') errors.push(`visible_not_boolean:${id}`);
  });
  ['campaign', 'rules'].forEach((id) => {
    const ox = cfg?.offsetX?.[id];
    if (ox !== undefined && (!Number.isFinite(Number(ox)) || Math.abs(Number(ox)) > 400)) {
      errors.push(`offsetX_invalid:${id}`);
    }
  });
  if (cfg?.visible?.campaign === false) errors.push('campaign_must_be_visible');
  if (cfg?.visible?.rules === false) errors.push('rules_must_be_visible');
  const hero = cfg?.heroTopSection;
  if (hero !== undefined && !HERO_TOP_SECTIONS.includes(String(hero).trim().toLowerCase())) {
    errors.push('heroTopSection_invalid');
  }
  return { valid: errors.length === 0, errors };
}

export function sanitizeClientLayoutConfig(rawConfig, options = {}) {
  const logger = typeof options.logger === 'function' ? options.logger : console.warn;
  const config = rawConfig && typeof rawConfig === 'object' ? rawConfig : {};
  const corrections = [];
  if (!rawConfig || typeof rawConfig !== 'object') corrections.push('config_not_object');

  const rawOrder = Array.isArray(config.order) ? [...config.order] : [...DEFAULT_CLIENT_LAYOUT_CONFIG.order];
  if (!Array.isArray(config.order)) corrections.push('order_missing_defaulted');
  const normalizedOrder = [];
  rawOrder.forEach((id) => {
    if (!CLIENT_LAYOUT_BLOCKS.includes(id)) {
      corrections.push(`order_invalid_id:${String(id)}`);
      return;
    }
    if (normalizedOrder.includes(id)) {
      corrections.push(`order_duplicate:${id}`);
      return;
    }
    normalizedOrder.push(id);
  });
  CLIENT_LAYOUT_BLOCKS.forEach((id) => {
    if (!normalizedOrder.includes(id)) {
      normalizedOrder.push(id);
      corrections.push(`order_missing_appended:${id}`);
    }
  });

  const sizes = { ...DEFAULT_CLIENT_LAYOUT_CONFIG.sizes };
  const highlight = { ...DEFAULT_CLIENT_LAYOUT_CONFIG.highlight };
  const style = { ...DEFAULT_CLIENT_LAYOUT_CONFIG.style };
  const visible = { ...DEFAULT_CLIENT_LAYOUT_CONFIG.visible };
  const offsetX = { ...DEFAULT_CLIENT_LAYOUT_CONFIG.offsetX };

  const srcSizes = config.sizes || {};
  const srcHighlight = config.highlight || {};
  const srcStyle = config.style || {};
  const srcVisible = config.visible || {};
  const srcOffsetX = config.offsetX || {};

  CLIENT_LAYOUT_BLOCKS.forEach((id) => {
    const size = srcSizes[id];
    if (size !== undefined) {
      const normalizedSize = String(size).trim().toLowerCase();
      if (CLIENT_LAYOUT_ALLOWED_SIZES.includes(normalizedSize)) {
        sizes[id] = normalizedSize;
      } else {
        corrections.push(`size_invalid:${id}`);
      }
    }
    const h = srcHighlight[id];
    if (h !== undefined) {
      if (typeof h === 'boolean') highlight[id] = h;
      else corrections.push(`highlight_not_boolean:${id}`);
    }
    const st = srcStyle[id];
    if (st !== undefined) {
      const normalizedStyle = String(st).trim().toLowerCase();
      if (CLIENT_LAYOUT_ALLOWED_STYLES.includes(normalizedStyle)) {
        style[id] = normalizedStyle;
      } else {
        corrections.push(`style_invalid:${id}`);
      }
    }
    const v = srcVisible[id];
    if (v !== undefined) {
      if (typeof v === 'boolean') visible[id] = v;
      else corrections.push(`visible_not_boolean:${id}`);
    }
  });
  ['campaign', 'rules'].forEach((id) => {
    const ox = srcOffsetX[id];
    if (ox !== undefined) {
      const n = Number(ox);
      if (Number.isFinite(n)) offsetX[id] = Math.max(-160, Math.min(160, n));
      else corrections.push(`offsetX_invalid:${id}`);
    }
  });

  if (visible.campaign !== true) {
    visible.campaign = true;
    corrections.push('campaign_forced_visible');
  }
  if (visible.rules !== true) {
    visible.rules = true;
    corrections.push('rules_forced_visible');
  }

  let heroTopSection = DEFAULT_CLIENT_LAYOUT_CONFIG.heroTopSection;
  const rawHero = config.heroTopSection;
  if (rawHero !== undefined) {
    const normalizedHero = String(rawHero).trim().toLowerCase();
    if (HERO_TOP_SECTIONS.includes(normalizedHero)) {
      heroTopSection = normalizedHero;
    } else {
      corrections.push('heroTopSection_invalid');
    }
  } else {
    heroTopSection = deriveHeroTopSection(normalizedOrder);
  }

  const orderWithHero = applyHeroTopSectionToOrder(normalizedOrder, heroTopSection);

  warnCorrections(corrections, logger);
  return { heroTopSection, order: orderWithHero, sizes, highlight, style, visible, offsetX };
}

export function normalizeClientLayoutConfig(rawConfig) {
  return sanitizeClientLayoutConfig(rawConfig);
}

export function normalizeClientLayoutConfigWithMeta(rawConfig, options = {}) {
  const sanitized = sanitizeClientLayoutConfig(rawConfig, options);
  const validation = validateClientLayoutConfigStrict(rawConfig);
  return { config: sanitized, corrections: validation.errors };
}

function toObject(value) {
  return value && typeof value === 'object' ? { ...value } : {};
}

export function legacyNormalizeClientLayoutConfig(rawConfig) {
  const config = rawConfig && typeof rawConfig === 'object' ? rawConfig : {};
  const order = Array.isArray(config.order) ? [...config.order] : [...DEFAULT_CLIENT_LAYOUT_CONFIG.order];
  const normalizedOrder = [];
  order.forEach((id) => {
    if (CLIENT_LAYOUT_BLOCKS.includes(id) && !normalizedOrder.includes(id)) normalizedOrder.push(id);
  });
  CLIENT_LAYOUT_BLOCKS.forEach((id) => {
    if (!normalizedOrder.includes(id)) normalizedOrder.push(id);
  });

  const sizes = toObject(DEFAULT_CLIENT_LAYOUT_CONFIG.sizes);
  const highlight = toObject(DEFAULT_CLIENT_LAYOUT_CONFIG.highlight);
  const style = toObject(DEFAULT_CLIENT_LAYOUT_CONFIG.style);
  const visible = toObject(DEFAULT_CLIENT_LAYOUT_CONFIG.visible);
  const offsetX = toObject(DEFAULT_CLIENT_LAYOUT_CONFIG.offsetX);

  const srcSizes = config.sizes || {};
  const srcHighlight = config.highlight || {};
  const srcStyle = config.style || {};
  const srcVisible = config.visible || {};
  const srcOffsetX = config.offsetX || {};

  CLIENT_LAYOUT_BLOCKS.forEach((id) => {
    if (typeof srcSizes[id] === 'string' && srcSizes[id].trim()) sizes[id] = srcSizes[id].trim();
    if (srcHighlight[id] !== undefined) highlight[id] = srcHighlight[id] === true;
    if (typeof srcStyle[id] === 'string' && srcStyle[id].trim()) style[id] = srcStyle[id].trim();
    if (srcVisible[id] !== undefined) visible[id] = srcVisible[id] === true;
  });
  ['campaign', 'rules'].forEach((id) => {
    if (srcOffsetX[id] !== undefined && Number.isFinite(Number(srcOffsetX[id]))) {
      offsetX[id] = Number(srcOffsetX[id]);
    }
  });

  const heroTopSection =
    src.heroTopSection !== undefined && HERO_TOP_SECTIONS.includes(String(src.heroTopSection).trim().toLowerCase())
      ? String(src.heroTopSection).trim().toLowerCase()
      : deriveHeroTopSection(normalizedOrder);
  const finalOrder = applyHeroTopSectionToOrder(normalizedOrder, heroTopSection);
  return { heroTopSection, order: finalOrder, sizes, highlight, style, visible, offsetX };
}

export function buildClientLayoutFromPreset(presetName) {
  const preset = CLIENT_LAYOUT_PRESETS[presetName];
  if (!preset) return sanitizeClientLayoutConfig(DEFAULT_CLIENT_LAYOUT_CONFIG);
  return sanitizeClientLayoutConfig({
    ...DEFAULT_CLIENT_LAYOUT_CONFIG,
    ...preset,
    sizes: { ...DEFAULT_CLIENT_LAYOUT_CONFIG.sizes, ...(preset.sizes || {}) },
    highlight: { ...DEFAULT_CLIENT_LAYOUT_CONFIG.highlight, ...(preset.highlight || {}) },
    style: { ...DEFAULT_CLIENT_LAYOUT_CONFIG.style, ...(preset.style || {}) },
    visible: { ...DEFAULT_CLIENT_LAYOUT_CONFIG.visible, ...(preset.visible || {}) },
    offsetX: { ...DEFAULT_CLIENT_LAYOUT_CONFIG.offsetX, ...(preset.offsetX || {}) },
    heroTopSection: preset.heroTopSection || DEFAULT_CLIENT_LAYOUT_CONFIG.heroTopSection
  });
}
